// src/api/userContext.js
/**
 * User-context API client.
 *
 * Talks to the regional API gateway (`GET /v1/users/me`), which proxies to the
 * backend User Service. The gateway verifies the caller's Firebase ID token
 * (attached by the axios request interceptor) and returns the authenticated
 * user's account context — including the dashboard-membership fields the
 * dashboard access gate relies on.
 *
 * Response shape (see pdx-backend-platform user-service `buildUserContextResponse`):
 *   {
 *     user:           { uid, email, roles: string[] },
 *     accountContext: {
 *       region,
 *       preferredLocale,
 *       dashboardMembershipRequired: boolean,
 *       dashboardMembershipStatus:   string,   // e.g. "not_provisioned" | "active"
 *       organisationId,
 *       organisationRole,
 *       appBasePath,
 *       dashboardBasePath,
 *     },
 *     profile, profileSummary, recordingModeConfig, contextMetadata, ...
 *   }
 */

import api from "./axiosInstance";

/** Public gateway route for the authenticated user's context. */
export const USER_CONTEXT_PATH = "/v1/users/me";

/**
 * Fetches the authenticated user's account/dashboard context from the gateway.
 *
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object>} The user-context payload.
 * @throws {import('axios').AxiosError} On network / non-2xx responses.
 */
export async function fetchUserContext({ signal } = {}) {
  const startedAt = Date.now();

  try {
    const { data } = await api.get(USER_CONTEXT_PATH, { signal });
    return data;
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error("[user-context-api] fetchUserContext failed", {
        elapsedMs: Date.now() - startedAt,
        status: error?.response?.status || null,
        code: error?.code || null,
        response: error?.response?.data || null,
        message: error?.message || null,
      });
    }
    throw error;
  }
}
